/**
 * Safari fallback for the `.hero-reveal` parallax (AW — item 25).
 *
 * `.hero-reveal` images normally animate via `animation-timeline: view()`
 * (see styles/animations.scss) — the image slides from top- to
 * bottom-aligned as its section scrolls through the viewport, travelling
 * `--reveal-travel` (a % of the image's own height, default 22.222%).
 * Safari has no `animation-timeline` support, so this module reproduces the
 * same motion with an IntersectionObserver gating a continuous rAF loop.
 *
 * Drive mechanism — two Safari constraints shape it:
 *   - Safari scrolls asynchronously on the compositor and delivers `scroll`
 *     events sparsely/late relative to the on-screen position, so an
 *     event-driven update stutters no matter how cheap it is. Instead a rAF
 *     loop runs continuously while any hero is intersecting (IO adds/removes
 *     from `inView`; the loop stops itself when the set empties). One
 *     getBoundingClientRect read per visible hero per frame; resize is
 *     covered for free since geometry is re-read every frame.
 *   - Per-frame inline `transform` writes repaint the full image unless the
 *     element is on its own compositor layer, so wiring sets
 *     `will-change: transform` (JS fallback path only — the native CSS path
 *     never runs this module).
 *
 * Bails entirely on:
 *   - browsers with native support (`CSS.supports("animation-timeline: view()")`) —
 *     the CSS-only path is untouched.
 *   - `prefers-reduced-motion: reduce` — the static centred crop
 *     (`.hero-reveal` base rule) stays put, matching the native path's own
 *     `@media (prefers-reduced-motion: reduce) { animation: none }`.
 *
 * Progress `p` is computed over the same range as CSS `animation-range: cover`:
 * p=0 when the element's top reaches the viewport bottom, p=1 when its
 * bottom reaches the viewport top. `transform = translateY(calc(<travel> * -p))`
 * is exactly the native `hero-reveal` keyframe interpolation, so p=0.5 lands
 * on -travel/2 — identical to the static base rule — and there's no visual
 * discontinuity at first paint or at the moment JS takes over from CSS.
 *
 * Lifecycle mirrors io-reveal.ts (bail conditions, element selector) but adds
 * teardown since this is a continuous scroll-driven effect rather than a
 * one-shot class toggle: observer + rAF loop are torn down on
 * htmx:before-swap, and the `wired` guard is CLEARED (not just checked) at
 * teardown — same pattern as embla.ts's `initialized` WeakSet-clearing fix
 * (5d2006f) — so a boosted nav back to a structurally identical section
 * re-initialises rather than being silently skipped.
 */

type State = { travel: number; lastPx: number };

const state = new WeakMap<HTMLElement, State>();
const wired = new Set<HTMLElement>();
const inView = new Set<HTMLElement>();

let observer: IntersectionObserver | null = null;
let rafId = 0;

const DEFAULT_TRAVEL = 22.222;

function readTravel(el: HTMLElement): number {
  const raw = getComputedStyle(el).getPropertyValue("--reveal-travel").trim();
  const pct = parseFloat(raw);
  return Number.isFinite(pct) ? pct : DEFAULT_TRAVEL;
}

// getBoundingClientRect() returns the VISUAL (post-transform) rect. We set
// translateY on this same element, so a naive re-read next frame would fold
// our own last transform into the "layout" position and drift. Read the
// currently-applied translateY off the computed transform matrix and
// subtract it back out before computing progress.
function currentTranslateY(el: HTMLElement): number {
  const t = getComputedStyle(el).transform;
  if (!t || t === "none") return 0;
  try {
    return new DOMMatrixReadOnly(t).m42;
  } catch {
    return 0;
  }
}

function update(): void {
  const vh = window.innerHeight;
  inView.forEach((el) => {
    const s = state.get(el);
    if (!s) return;
    const rect = el.getBoundingClientRect();
    const layoutTop = rect.top - s.lastPx;
    const total = vh + rect.height;
    const p = total > 0 ? Math.max(0, Math.min(1, (vh - layoutTop) / total)) : 0;
    const px = (s.travel / 100) * rect.height * -p;
    el.style.transform = `translateY(${px}px)`;
    s.lastPx = px;
  });
}

function tick(): void {
  update();
  rafId = inView.size ? requestAnimationFrame(tick) : 0;
}

function startLoop(): void {
  if (!rafId && inView.size) rafId = requestAnimationFrame(tick);
}

function ensureObserver(): IntersectionObserver {
  if (!observer) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) inView.add(entry.target as HTMLElement);
        else inView.delete(entry.target as HTMLElement);
      });
      startLoop();
    });
  }
  return observer;
}

export function initHeroParallax(): void {
  if (CSS.supports("animation-timeline: view()")) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const els = document.querySelectorAll<HTMLElement>(".hero-reveal");
  if (!els.length) return;

  const obs = ensureObserver();
  els.forEach((el) => {
    if (wired.has(el)) return;
    wired.add(el);
    el.style.willChange = "transform";
    state.set(el, { travel: readTravel(el), lastPx: currentTranslateY(el) });
    obs.observe(el);
  });
}

export function teardownHeroParallax(): void {
  observer?.disconnect();
  cancelAnimationFrame(rafId);
  rafId = 0;
  wired.forEach((el) => {
    el.style.transform = "";
    el.style.willChange = "";
  });
  wired.clear();
  inView.clear();
}

// Initial load is triggered by the explicit initHeroParallax() call in
// scripts/index.ts (next to initIoReveal()) — same split as io-reveal.ts,
// which has no DOMContentLoaded listener of its own either.
document.addEventListener("htmx:load", initHeroParallax);
document.addEventListener("htmx:before-swap", teardownHeroParallax);
